// Test Skill Modal — allows admins to preview a skill's effect on a sample query

import { BookOpen, Brain, Loader2, Play, X } from 'lucide-react';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { AiSkill } from '../../services/aiSkillsService';
import { supabase } from '../../utils/supabaseClient';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const DEFAULT_QUESTION = 'What should I do if a player shows signs of concussion?';

type TestSkillModalProps = {
  skill: AiSkill;
  onClose: () => void;
};

type TestResult = {
  content: string;
  rawContent: string;
  sources: string[];
  skillApplied: boolean;
  skillName: string;
};

export const TestSkillModal = ({ skill, onClose }: TestSkillModalProps) => {
  const [question, setQuestion] = useState(DEFAULT_QUESTION);
  const [provider, setProvider] = useState<'knowledge-base' | 'certant-ai'>('knowledge-base');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TestResult | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  const handleRunTest = async () => {
    if (!question.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const response = await fetch(`${API_BASE_URL}/api/charlie-chat/test-skill`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          skillId: skill.id,
          provider,
          question: question.trim(),
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || err.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setResult(data.data as TestResult);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Test failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold">Test Skill</h3>
            <p className="text-sm text-muted-foreground">
              Testing: <strong>{skill.skill_name}</strong>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Question input */}
          <div>
            <label className="block text-sm font-medium mb-1">Sample Question</label>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-y"
            />
          </div>

          {/* Provider selector */}
          <div>
            <label className="block text-sm font-medium mb-1">Provider</label>
            <div className="flex gap-2">
              <button
                onClick={() => setProvider('knowledge-base')}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${
                  provider === 'knowledge-base'
                    ? 'border-primary bg-primary/10 text-primary font-medium'
                    : 'border-border text-muted-foreground hover:bg-muted/50'
                }`}
              >
                <BookOpen className="w-4 h-4" />
                Knowledge Base
              </button>
              <button
                onClick={() => setProvider('certant-ai')}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${
                  provider === 'certant-ai'
                    ? 'border-primary bg-primary/10 text-primary font-medium'
                    : 'border-border text-muted-foreground hover:bg-muted/50'
                }`}
              >
                <Brain className="w-4 h-4" />
                Certant AI
              </button>
            </div>
          </div>

          {/* Run button */}
          <button
            onClick={handleRunTest}
            disabled={loading || !question.trim()}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            {loading ? 'Running test…' : 'Run Test'}
          </button>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30 text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">Result</h4>
                {result.skillApplied && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowRaw(false)}
                      className={`text-xs px-2 py-0.5 rounded transition-colors ${
                        !showRaw
                          ? 'bg-primary/15 text-primary font-medium'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Skill-Enhanced
                    </button>
                    <button
                      onClick={() => setShowRaw(true)}
                      className={`text-xs px-2 py-0.5 rounded transition-colors ${
                        showRaw
                          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-medium'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Raw
                    </button>
                  </div>
                )}
              </div>

              <div className="border border-border rounded-lg p-4 bg-muted/10 max-h-64 overflow-y-auto">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>
                    {showRaw ? result.rawContent : result.content}
                  </ReactMarkdown>
                </div>
              </div>

              {result.sources.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Sources: {result.sources.join(', ')}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
